// TermProgressBar — 6px violet bar with today marker and hatched
// extension overlays. Visualises where a contract sits in its lifecycle.
// Header text humanises elapsed/remaining; footer surfaces the possible
// extended-end date when extension options are configured.

import {
  computeTermProgress,
  computeExtensionOverlays,
} from "../../../lib/contract-overview/termProgress";
import { extensionPeriodSummary } from "../../../lib/contract-overview/extensionPeriodSummary";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import type { ManagedContract } from "../../../types";

interface TermProgressBarProps {
  contract: ManagedContract;
  today?: Date;
  /** When false, suppresses the start/end date caption beneath the bar.
   *  Callers that show the same dates as labelled rows in a field grid
   *  pass `false` so the information isn't duplicated. Defaults to true
   *  for backward compatibility. */
  showDateCaption?: boolean;
}

export function TermProgressBar({
  contract,
  today,
  showDateCaption = true,
}: TermProgressBarProps) {
  const progress = computeTermProgress(contract, today);
  const overlays = computeExtensionOverlays(contract);

  // Sparse rows without start/end dates derive no term — render nothing.
  if (!progress) return null;

  const showMarker = progress.elapsedPct > 0 && progress.elapsedPct < 100;

  const ariaLabel = `Contract progress: ${progress.headerText}`;

  return (
    <div className="space-y-2">
      <p className="text-caption text-slate-600 dark:text-slate-400">
        {progress.headerText}
      </p>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress.elapsedPct}
        aria-label={ariaLabel}
        className="relative w-full"
      >
        <div
          data-testid="term-bar-track"
          className="relative h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden"
        >
          {overlays.map((o) => (
            <div
              key={o.periodNumber}
              data-testid="term-bar-extension"
              data-status={o.status}
              className={
                o.status === "exercised"
                  ? "absolute top-0 h-full bg-brand-300 dark:bg-brand-700"
                  : o.status === "expired"
                    ? "absolute top-0 h-full bg-slate-200 dark:bg-slate-600 opacity-60"
                    : "absolute top-0 h-full bg-brand-200/30 dark:bg-brand-800/30 [background-image:repeating-linear-gradient(45deg,currentColor_0,currentColor_1px,transparent_1px,transparent_4px)] text-brand-400/40"
              }
              style={{ left: `${o.startPct}%`, width: `${o.widthPct}%` }}
            />
          ))}
          <div
            data-testid="term-bar-fill"
            className="absolute top-0 left-0 h-full rounded-full bg-brand-600 dark:bg-brand-500"
            style={{ width: `${progress.elapsedPct}%` }}
          />
        </div>
        {showMarker && (
          <div
            data-testid="term-bar-marker"
            className="absolute h-3 w-3 rounded-full border-2 border-brand-600 dark:border-brand-400 bg-white dark:bg-slate-800 -translate-x-1/2"
            style={{
              left: `${progress.elapsedPct}%`,
              top: "-3px",
            }}
            aria-hidden="true"
          />
        )}
      </div>

      {showDateCaption && (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          {formatDayMonthYear(contract.startDate)}{" "}
          <span aria-hidden="true">·</span>{" "}
          {formatDayMonthYear(contract.endDate)}
        </p>
      )}

      {overlays.length > 0 && (
        <p className="text-caption italic text-slate-500 dark:text-slate-400">
          Possible end {formatDayMonthYear(progress.possibleEndDate)} ·{" "}
          {extensionPeriodSummary(contract.extensionOptions)}
        </p>
      )}
    </div>
  );
}

export default TermProgressBar;
